import { useCallback, useContext, useMemo } from 'react';

import { AuthContext, ROLES } from '../../constants';

/**
 *   @returns {Object} Permission utilities
 *   @property {string[]} userRoles                    - Raw array of roles from AuthContext
 *   @property {boolean}  isRestrictedUser             - true if user has role USER or AGENCY
 *   @property {boolean}  canPerformAdminActions       - true if user is allowed to do bulk/admin actions (recommended for query enabled flags)
 *   @property {(role: string) => boolean} hasRole     - Helper to check if user has a specific role
 *   @property {boolean}  isAdmin                      - true if user has ADMIN or SUPER_ADMIN role
 *   @property {boolean}  isRecruiter                  - true if user has RECRUITER role
 *
 */
export const useUserPermissions = () => {
  const { userInfo } = useContext(AuthContext);
  const userRoles = userInfo?.roles || [];

  const isRestrictedUser = userRoles.includes(ROLES.USER) || userRoles.includes(ROLES.AGENCY);
  const canPerformAdminActions = !isRestrictedUser;

  const hasRole = useCallback((role) => userRoles.includes(role), [userRoles]);
  const isAdmin =
    userRoles.includes(ROLES.ADMIN) ||
    userRoles.includes(ROLES.SUPER_ADMIN) ||
    userRoles.includes(ROLES.TENANT_OWNER);
  const isRecruiter = userRoles.includes(ROLES.RECRUITER);

  return useMemo(
    () => ({
      userRoles,
      isRestrictedUser,
      canPerformAdminActions,
      hasRole,
      isAdmin,
      isRecruiter,
      userInfo,
    }),
    [userRoles, isRestrictedUser, canPerformAdminActions, hasRole, isAdmin, isRecruiter, userInfo]
  );
};
